import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { getInvitationByToken, processInvitationForExistingUser, type Invitation } from '@/services/invitationService';
import { organizationService } from '@/firebase/organizationService';
import { formatLoginIdentifierForDisplay } from '@/utils/staffUsernameAuth';
import { AlertCircle, CheckCircle, Mail, User, Shield, Building2 } from 'lucide-react';

const AcceptInvitationPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [invitation, setInvitation] = useState<Invitation | null>(null);
  const [organizationName, setOrganizationName] = useState<string | null>(null);

  useEffect(() => {
    const validateInvitation = async () => {
      // Get invitation token from URL
      const token = searchParams.get('token');
      if (!token) {
        setError('No invitation token found in URL');
        setLoading(false);
        return;
      }

      try {
        // Validate the invitation token
        const result = await getInvitationByToken(token);
        if (!result.success || !result.invitation) {
          setError(result.error || 'Invalid invitation link');
          setLoading(false);
          return;
        }

        setInvitation(result.invitation);
        
        // Fetch organization name if organizationId is provided
        if (result.invitation.organizationId) {
          try {
            const orgResult = await organizationService.getOrganization(result.invitation.organizationId);
            if (orgResult.success && orgResult.data) {
              setOrganizationName(orgResult.data.name);
            }
          } catch (orgError) {
            console.error('Error fetching organization:', orgError);
          }
        }
        
        // If user is already authenticated, check if it's the right email
        if (user) {
          console.log('👤 User is authenticated:', {
            id: user.id,
            email: user.email,
            invitationEmail: result.invitation.email
          });
          
          if (user.email?.toLowerCase() === result.invitation.email.toLowerCase()) {
            // Same user - process invitation immediately for existing logged-in user
            console.log('✅ Email matches, processing invitation for existing user...');
            try {
              const processResult = await processInvitationForExistingUser(token, user.id);
              if (processResult.success) {
                console.log('✅ Invitation processed for logged-in user');
                // Redirect to home after a short delay to show success
                setTimeout(() => {
                  window.location.href = '/home';
                }, 1500);
              } else {
                console.error('❌ Failed to process invitation:', processResult.error);
                setError(processResult.error || 'Failed to process invitation');
                setLoading(false);
              }
            } catch (processError: any) {
              console.error('❌ Error processing invitation:', processError);
              console.error('❌ Error details:', {
                message: processError.message,
                stack: processError.stack,
                code: processError.code
              });
              setError('Failed to process invitation. Please try again.');
              setLoading(false);
            }
          } else {
            // Different user - show error
            console.log('❌ Email mismatch:', {
              userEmail: user.email,
              invitationEmail: result.invitation.email
            });
            setError('This invitation is for a different account. Please log out first.');
            setLoading(false);
          }
        } else {
          console.log('👤 User is not authenticated, waiting for user to click accept button...');
          setLoading(false);
        }
      } catch (err: any) {
        console.error('Error validating invitation:', err);
        setError('Failed to validate invitation. Please try again.');
        setLoading(false);
      }
    };

    validateInvitation();
  }, [searchParams, user]);

  const handleAcceptInvitation = () => {
    const token = searchParams.get('token');
    if (!token || !invitation) {
      setError('Invalid invitation link');
      return;
    }

    // Redirect to login page with invitation token
    window.location.href = `/login?invitation=${token}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Validating invitation...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          {/* Complin Logo */}
          <div className="flex items-center justify-center space-x-2 mb-6">
            <div className="w-5 h-5 rounded-full bg-black flex-shrink-0"></div>
            <span className="text-xl font-bold text-gray-900 lowercase" style={{ letterSpacing: '-0.5px', fontFamily: 'Inter, sans-serif' }}>complin</span>
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Accept Invitation
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            You've been invited to join this workspace
          </p>
        </div>
        
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md flex items-start">
            <AlertCircle className="h-5 w-5 text-red-500 mr-2 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium">Error</p>
              <p className="text-sm">{error}</p>
            </div>
          </div>
        )}

        {invitation && !error && (
          <div className="bg-white shadow-md rounded-lg p-6 space-y-4">
            <div className="flex items-center space-x-2 text-green-600 mb-4">
              <CheckCircle className="h-5 w-5" />
              <span className="font-medium">Valid Invitation</span>
            </div>

            <div className="space-y-3">
              {organizationName && (
                <div className="flex items-start">
                  <Building2 className="h-5 w-5 text-gray-400 mr-3 mt-0.5" />
                  <div>
                    <p className="text-xs text-gray-500">Organization</p>
                    <p className="text-sm font-medium text-gray-900">{organizationName}</p>
                  </div>
                </div>
              )}

              <div className="flex items-start">
                <Mail className="h-5 w-5 text-gray-400 mr-3 mt-0.5" />
                <div>
                  <p className="text-xs text-gray-500">Username / email</p>
                  <p className="text-sm font-medium text-gray-900">
                    {formatLoginIdentifierForDisplay(invitation.email)}
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <User className="h-5 w-5 text-gray-400 mr-3 mt-0.5" />
                <div>
                  <p className="text-xs text-gray-500">Name</p>
                  <p className="text-sm font-medium text-gray-900">{invitation.name}</p>
                </div>
              </div>

              <div className="flex items-start">
                <Shield className="h-5 w-5 text-gray-400 mr-3 mt-0.5" />
                <div>
                  <p className="text-xs text-gray-500">Role</p>
                  <p className="text-sm font-medium text-gray-900 capitalize">{invitation.role}</p>
                </div>
              </div>
            </div>

            <div className="pt-4 border-t border-gray-200">
              <p className="text-sm text-gray-600 mb-4">
                {user 
                  ? 'You are already logged in. Click below to accept this invitation and add this organization to your account.' 
                  : 'Click Continue below. If you already have an account, you\'ll sign in with your password. If you don\'t have an account yet, you\'ll create one.'}
              </p>
              <button
                onClick={handleAcceptInvitation}
                className="w-full bg-primary-600 hover:bg-primary-700 text-white font-medium py-2 px-4 rounded-md transition-colors"
              >
                Continue
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AcceptInvitationPage;

